import React from 'react';
import { XIcon } from 'lucide-react';
interface Record {
  id: string;
  borrowerName: string;
  loanAmount: number;
  date: string;
  status: string;
  propertyAddress?: string;
}
interface RecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  stage: string;
  records: Record[];
  bucketName: string;
}
export function RecordModal({
  isOpen,
  onClose,
  stage,
  records,
  bucketName
}: RecordModalProps) {
  if (!isOpen) return null;
  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      Active: 'bg-green-100 text-green-800 border-green-200',
      Pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'On Hold': 'bg-orange-100 text-orange-800 border-orange-200',
      Completed: 'bg-blue-100 text-blue-800 border-blue-200',
      'In Progress': 'bg-indigo-100 text-indigo-800 border-indigo-200'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-110">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-111">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-112"></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-113">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full" data-id="element-114">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5" data-id="element-115">
            <div className="flex items-center justify-between" data-id="element-116">
              <div data-id="element-117">
                <h3 className="text-xl font-bold text-white" id="modal-title" data-id="element-118">
                  {stage} Records
                </h3>
                <p className="text-sm text-blue-100 mt-1" data-id="element-119">
                  {bucketName} • {records.length}{' '}
                  {records.length === 1 ? 'record' : 'records'}
                </p>
              </div>
              <button onClick={onClose} className="rounded-lg p-2 text-blue-100 hover:text-white hover:bg-blue-500 transition-all" aria-label="Close modal" data-id="element-120">
                <XIcon className="h-5 w-5" data-id="element-121" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-gray-50 px-6 py-6 max-h-[600px] overflow-y-auto" data-id="element-122">
            {records.length > 0 ? <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm bg-white" data-id="element-123">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-124">
                  <thead className="bg-gradient-to-b from-gray-50 to-gray-100" data-id="element-125">
                    <tr data-id="element-126">
                      <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-127">
                        Loan ID
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-128">
                        Borrower Name
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-129">
                        Property Address
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-130">
                        Loan Amount
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-131">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-132">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100" data-id="element-133">
                    {records.map(record => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-134">
                        <td className="px-6 py-4 text-sm text-gray-900 font-mono font-semibold" data-id="element-135">
                          {record.id}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 font-semibold" data-id="element-136">
                          {record.borrowerName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-137">
                          {record.propertyAddress || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-bold" data-id="element-138">
                          ${record.loanAmount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 font-medium" data-id="element-139">
                          {record.date}
                        </td>
                        <td className="px-6 py-4" data-id="element-140">
                          <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold border ${getStatusColor(record.status)}`} data-id="element-141">
                            {record.status}
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div> : <div className="text-center py-16 bg-white rounded-xl border border-gray-200" data-id="element-142">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-143">
                  <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-144">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" data-id="element-145" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-146">
                  No records found
                </p>
                <p className="text-sm text-gray-500" data-id="element-147">
                  There are no records for this stage yet.
                </p>
              </div>}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200" data-id="element-148">
            <button onClick={onClose} className="w-full sm:w-auto px-8 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 active:bg-blue-800 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-sm hover:shadow" data-id="element-149">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>;
}